import {Injectable} from '@angular/core';
import {HttpClient, HttpContext} from '@angular/common/http';
import {concat, defer, Observable, of} from 'rxjs';
import {concatMap, map, reduce} from 'rxjs/operators';
import {ApiClientService, IGNORE_BAD_REQUEST} from '../api-client/api-client.service';

export interface ApiCsvExportOptions {
  /** API route after `/api/gizmo`, e.g. `/statistics/conversion/download`. */
  endpoint: string;
  /** Report filters; must include `startDate` and `endDate` as `yyyy-MM-dd` strings. */
  params: Record<string, any>;
  /** Final filename (with or without `.csv`). */
  filename: string;
  /** Number of days per request. Default: 7. */
  chunkDays?: number;
}

export type CsvExportEvent =
  | { type: 'progress'; current: number; total: number }
  | { type: 'complete'; chunkCount: number; truncatedChunks: number };

interface DayRange {
  startDate: string;
  endDate: string;
}

interface ChunkResult {
  text: string;
  truncated: boolean;
  /** The request asked the server for the header row. */
  askedHeaders: boolean;
}

interface ExportContext {
  options: ApiCsvExportOptions;
  headersEmitted: boolean;
}

const DEFAULT_CHUNK_DAYS = 7;
const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;
const TRUNCATION_MARKER = 'WARNING: Export interrupted';

@Injectable({
  providedIn: 'root'
})
export class CsvExportService {

  constructor(
    private apiClient: ApiClientService,
    private http: HttpClient
  ) {
  }

  download(filename: string, headers: string[], rows: any[][]): void {
    const csvRows = [headers, ...rows].map(row => row.map(cell => this.cell(cell)).join(','));

    // Prepend a UTF-8 BOM so Excel detects the encoding and renders accents correctly.
    const bom = String.fromCharCode(0xFEFF);
    this.triggerDownload([bom + csvRows.join('\n')], filename);
  }

  /**
   * Export the whole filtered dataset, straight from the API, instead of the rows
   * currently displayed. The range is split into chunks because the server stops
   * streaming a single request after 25 seconds.
   */
  exportFromApi(options: ApiCsvExportOptions): Observable<CsvExportEvent> {
    return new Observable<CsvExportEvent>(subscriber => {
      let ranges: DayRange[];

      // Reported through the subscriber, so a caller never has to catch it separately
      try {
        ranges = this.buildDayChunks(
          options.params['startDate'],
          options.params['endDate'],
          options.chunkDays ?? DEFAULT_CHUNK_DAYS
        );
      } catch (error) {
        subscriber.error(error);
        return;
      }

      const total = ranges.length;
      const context: ExportContext = {options, headersEmitted: false};
      const parts: BlobPart[] = [];
      let bomEmitted = false;
      let truncatedChunks = 0;
      let current = 0;

      // Initial progress so the UI can render "0 / N" before the first round-trip
      subscriber.next({type: 'progress', current: 0, total});

      const subscription = concat(
        ...ranges.map((range: DayRange) => this.fetchRange(context, range))
      ).subscribe({
        next: (chunk) => {
          if (chunk.text.length > 0) {
            // HttpClient (responseType: 'text') strips the UTF-8 BOM returned by the
            // server, so Excel would no longer detect UTF-8: re-emit it once.
            if (!bomEmitted) {
              if (chunk.text.charCodeAt(0) !== 0xFEFF) {
                parts.push('\uFEFF');
              }
              bomEmitted = true;
            }

            parts.push(chunk.text);
          }

          if (chunk.truncated) {
            truncatedChunks++;
          }

          current++;
          subscriber.next({type: 'progress', current, total});
        },
        complete: () => {
          this.triggerDownload(parts, this.normalizeFilename(options.filename));
          subscriber.next({type: 'complete', chunkCount: total, truncatedChunks});
          subscriber.complete();
        },
        error: (error) => subscriber.error(error)
      });

      return () => subscription.unsubscribe();
    });
  }

  formatMoney(value: any): string {
    const input = parseFloat(value);
    return isNaN(input) ? '' : (input / 100).toFixed(2);
  }

  formatPercent(value: any): string {
    const input = parseFloat(value);
    return isNaN(input) ? '' : (input * 100).toFixed(2);
  }

  /**
   * Fetch one range, and if the server had to stop mid-range, retry it as two smaller
   * ranges instead of losing the rows it could not write.
   */
  private fetchRange(context: ExportContext, range: DayRange): Observable<ChunkResult> {
    return defer(() => this.fetchChunk(context, range)).pipe(
      concatMap((result: ChunkResult) => {
        const halves = result.truncated ? this.splitRange(range) : null;

        if (halves === null) {
          // Kept as is: either complete, or a single day the server cannot finish
          if (result.askedHeaders && result.text.length > 0) {
            context.headersEmitted = true;
          }

          return of(result);
        }

        // The partial output is dropped, the halves re-export those days from scratch
        return concat(...halves.map((half: DayRange) => this.fetchRange(context, half))).pipe(
          reduce(
            (merged: ChunkResult, chunk: ChunkResult) => ({
              text: merged.text + chunk.text,
              truncated: merged.truncated || chunk.truncated,
              askedHeaders: merged.askedHeaders || chunk.askedHeaders
            }),
            {text: '', truncated: false, askedHeaders: false}
          )
        );
      })
    );
  }

  private fetchChunk(context: ExportContext, range: DayRange): Observable<ChunkResult> {
    const askedHeaders = !context.headersEmitted;

    const params: Record<string, any> = {
      ...context.options.params,
      startDate: range.startDate,
      endDate: range.endDate,
      withHeaders: askedHeaders ? 'true' : 'false'
    };

    // The report list sends those, the export must never be paginated
    delete params['page'];
    delete params['count'];

    return this.http
      .get(this.apiClient.getUrl(context.options.endpoint, params), {
        responseType: 'text',
        // The export reports its own failure, no need for the generic error snackbar
        context: new HttpContext().set(IGNORE_BAD_REQUEST, true)
      })
      .pipe(map((text: string) => ({...this.stripWarningLine(text), askedHeaders})));
  }

  /** Two halves of a multi-day range, or null when it is already a single day. */
  private splitRange(range: DayRange): DayRange[] | null {
    const start = this.parseDay(range.startDate) as number;
    const end = this.parseDay(range.endDate) as number;

    const days = Math.round((end - start) / MILLISECONDS_IN_DAY) + 1;
    if (days < 2) {
      return null;
    }

    const middle = start + (Math.floor(days / 2) - 1) * MILLISECONDS_IN_DAY;

    return [
      {startDate: this.formatDay(start), endDate: this.formatDay(middle)},
      {startDate: this.formatDay(middle + MILLISECONDS_IN_DAY), endDate: this.formatDay(end)}
    ];
  }

  private stripWarningLine(text: string): { text: string; truncated: boolean } {
    if (!text) {
      return {text: '', truncated: false};
    }

    // The server appends the WARNING line as the very last row when its timer fires
    const hadTrailingNewline = text.endsWith('\n');
    const body = hadTrailingNewline ? text.slice(0, -1) : text;

    const newlineIndex = body.lastIndexOf('\n');
    const lastLine = newlineIndex === -1 ? body : body.slice(newlineIndex + 1);

    if (!lastLine.startsWith(TRUNCATION_MARKER) && !lastLine.startsWith('"' + TRUNCATION_MARKER)) {
      return {text, truncated: false};
    }

    if (newlineIndex === -1) {
      return {text: '', truncated: true};
    }

    // Keep the trailing newline so concatenation with the next chunk stays clean
    return {text: body.slice(0, newlineIndex + 1), truncated: true};
  }

  /**
   * Split `yyyy-MM-dd` bounds into whole-day windows. Day granularity matters: the API
   * turns those bounds into day boundaries in the affiliate timezone, so chunking must
   * never cut inside a day.
   */
  private buildDayChunks(startDate: any, endDate: any, chunkDays: number): DayRange[] {
    const start = this.parseDay(startDate);
    const end = this.parseDay(endDate);

    if (start === null || end === null) {
      throw new Error('CsvExportService: startDate and endDate are required in params.');
    }

    if (start > end) {
      throw new Error('CsvExportService: startDate must be before or equal to endDate.');
    }

    if (chunkDays <= 0) {
      throw new Error('CsvExportService: chunkDays must be greater than 0.');
    }

    const ranges: DayRange[] = [];

    let cursor = start;
    while (cursor <= end) {
      const chunkEnd = Math.min(cursor + (chunkDays - 1) * MILLISECONDS_IN_DAY, end);

      ranges.push({
        startDate: this.formatDay(cursor),
        endDate: this.formatDay(chunkEnd)
      });

      // Both bounds are inclusive server-side, so the next chunk starts the day after
      cursor = chunkEnd + MILLISECONDS_IN_DAY;
    }

    return ranges;
  }

  /** UTC midnight, so day arithmetic never trips on a DST transition. */
  private parseDay(value: any): number | null {
    if (!value) {
      return null;
    }

    const day = value instanceof Date
      ? Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())
      : Date.parse(String(value).slice(0, 10) + 'T00:00:00Z');

    return isNaN(day) ? null : day;
  }

  private formatDay(timestamp: number): string {
    return new Date(timestamp).toISOString().slice(0, 10);
  }

  private triggerDownload(parts: BlobPart[], filename: string): void {
    const blob = new Blob(parts, {type: 'text/csv;charset=utf-8;'});
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  private normalizeFilename(filename: string): string {
    return filename.toLowerCase().endsWith('.csv') ? filename : `${filename}.csv`;
  }

  private cell(value: any): string {
    const text = value === null || value === undefined ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  }
}
